import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
    ArrowLeft, MapPin, Leaf, PawPrint, Lightbulb, NavigationArrow, Binoculars
} from '@phosphor-icons/react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons (leaflet + webpack issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function createColoredIcon(color) {
    return L.divIcon({
        className: '',
        html: `<div style="width:28px;height:28px;border-radius:50%;background:${color};border:3px solid #0f172a;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
        popupAnchor: [0, -16]
    });
}

function FlyToLocation({ lat, lng }) {
    const map = useMap();
    useEffect(() => {
        if (lat && lng) {
            map.flyTo([lat, lng], 9, { duration: 1.2 });
        }
    }, [lat, lng, map]);
    return null;
}

function HabitatMap({ locations, accent, flyTo, habitatId }) {
    const bounds = locations.length > 0
        ? L.latLngBounds(locations.map(l => [l.lat, l.lng])).pad(0.5)
        : L.latLngBounds([[-10, 112], [-44, 154]]);
    const icon = createColoredIcon(accent);

    return (
        <div className="rounded-xl overflow-hidden border-4 border-[#0f172a]" style={{ height: 350 }} data-testid="habitat-map">
            <MapContainer
                bounds={bounds}
                scrollWheelZoom={false}
                style={{ height: '100%', width: '100%' }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {locations.map((loc, i) => (
                    <Marker key={i} position={[loc.lat, loc.lng]} icon={icon}>
                        <Popup>
                            <div style={{ minWidth: 180 }}>
                                <p style={{ fontWeight: 'bold', fontSize: 14, margin: '0 0 4px' }}>{loc.name}</p>
                                <p style={{ fontSize: 12, color: '#475569', margin: '0 0 4px' }}>{loc.state} · Near {loc.nearest_city}</p>
                                <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 6px' }}>{loc.description}</p>
                                {habitatId && (
                                    <a
                                        href={`/habitats/${habitatId}/locations/${i}`}
                                        style={{ fontSize: 12, fontWeight: 'bold', color: accent, textDecoration: 'none' }}
                                    >
                                        Explore Wildlife &rarr;
                                    </a>
                                )}
                            </div>
                        </Popup>
                    </Marker>
                ))}
                {flyTo && <FlyToLocation lat={flyTo.lat} lng={flyTo.lng} />}
            </MapContainer>
        </div>
    );
}

export default function HabitatDetailPage() {
    const { habitatId } = useParams();
    const { api } = useAuth();
    const [habitat, setHabitat] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [flyTo, setFlyTo] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api().get(`/habitats/${habitatId}`);
                setHabitat(res.data);
            } catch (err) {
                setError(err.response?.data?.detail || 'Failed to load habitat');
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [habitatId]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#fefce8] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error || !habitat) {
        return (
            <div className="min-h-screen bg-[#fefce8] flex items-center justify-center p-4">
                <div className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-8 text-center max-w-md">
                    <p className="font-fredoka text-xl font-bold text-[#0f172a] mb-2">Habitat Locked</p>
                    <p className="text-[#475569] mb-4">{error}</p>
                    <Link to="/habitats" className="bg-[#22c55e] text-white border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-6 py-3 inline-block hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] transition-all">
                        Back to Habitats
                    </Link>
                </div>
            </div>
        );
    }

    const theme = habitat.theme || { bg: '#f0fdf4', accent: '#22c55e', text: '#0f172a', card_bg: '#fff' };

    return (
        <div className="min-h-screen" style={{ backgroundColor: theme.bg }}>
            {/* Nav */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#0f172a]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <Link to="/habitats" className="flex items-center gap-2" data-testid="back-habitats">
                            <div className="w-10 h-10 rounded-full border-4 border-[#0f172a] flex items-center justify-center" style={{ backgroundColor: theme.accent }}>
                                <span className="text-xl">{habitat.emoji}</span>
                            </div>
                            <span className="font-fredoka text-xl font-bold text-[#0f172a] hidden sm:block">{habitat.name}</span>
                        </Link>
                        <Link to="/habitats" className="bg-white text-[#0f172a] border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-4 py-2 text-sm flex items-center gap-1.5 hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] transition-all">
                            <ArrowLeft weight="bold" size={16} /> All Habitats
                        </Link>
                    </div>
                </div>
            </nav>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-24">
                {/* Hero Banner */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-3xl overflow-hidden mb-10"
                >
                    <div className="h-64 md:h-80 relative">
                        <img
                            src={habitat.image_url}
                            alt={habitat.name}
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${theme.accent}dd, transparent 60%)` }} />
                        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                            <span className="text-5xl mb-2 block">{habitat.emoji}</span>
                            <h1 className="font-fredoka text-3xl md:text-4xl font-bold text-white drop-shadow-lg">{habitat.name}</h1>
                            <p className="text-white/90 text-lg font-semibold mt-2 max-w-2xl drop-shadow">{habitat.description}</p>
                        </div>
                    </div>
                </motion.div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Left column: Map + Locations */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Map */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl overflow-hidden"
                            style={{ backgroundColor: theme.card_bg }}
                        >
                            <div className="p-5 border-b-4 border-[#0f172a]" style={{ backgroundColor: theme.bg }}>
                                <h2 className="font-fredoka text-xl font-bold flex items-center gap-2" style={{ color: theme.text }}>
                                    <NavigationArrow weight="bold" size={22} style={{ color: theme.accent }} />
                                    Where to Find This Habitat
                                </h2>
                            </div>
                            <div className="p-5">
                                <HabitatMap
                                    locations={habitat.locations || []}
                                    accent={theme.accent}
                                    flyTo={flyTo}
                                    habitatId={habitatId}
                                />
                                {/* Location cards */}
                                <div className="space-y-3 mt-4">
                                    {(habitat.locations || []).map((loc, i) => (
                                        <motion.div
                                            key={i}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.2 + i * 0.1 }}
                                            className="border-4 border-[#0f172a] rounded-xl p-4 flex items-start gap-4 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_#0f172a] transition-all"
                                            style={{ backgroundColor: theme.card_bg }}
                                            data-testid={`location-${i}`}
                                        >
                                            <div className="w-10 h-10 rounded-full flex-shrink-0 border-4 border-[#0f172a] flex items-center justify-center" style={{ backgroundColor: theme.accent }}>
                                                <MapPin weight="bold" size={18} className="text-white" />
                                            </div>
                                            <div>
                                                <h3 className="font-fredoka font-bold" style={{ color: theme.text }}>{loc.name}</h3>
                                                <p className="text-sm font-bold opacity-60" style={{ color: theme.text }}>
                                                    {loc.state} · Near {loc.nearest_city}
                                                </p>
                                                <p className="text-sm mt-1" style={{ color: `${theme.text}99` }}>{loc.description}</p>
                                                <div className="flex flex-wrap items-center gap-3 mt-2">
                                                    <button
                                                        onClick={() => setFlyTo({ lat: loc.lat, lng: loc.lng, _t: Date.now() })}
                                                        className="text-xs font-bold flex items-center gap-1 hover:underline"
                                                        style={{ color: theme.accent }}
                                                        data-testid={`view-on-map-${i}`}
                                                    >
                                                        <MapPin weight="bold" size={12} /> View on Map
                                                    </button>
                                                    <Link
                                                        to={`/habitats/${habitatId}/locations/${i}`}
                                                        className="text-xs font-bold flex items-center gap-1 px-2.5 py-1 rounded-lg border-2 border-[#0f172a] text-white hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_#0f172a] transition-all"
                                                        style={{ backgroundColor: theme.accent }}
                                                        data-testid={`explore-wildlife-${i}`}
                                                    >
                                                        <Binoculars weight="bold" size={12} /> Explore Wildlife
                                                    </Link>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        {/* Animals */}
                        {habitat.animals && habitat.animals.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl overflow-hidden"
                                style={{ backgroundColor: theme.card_bg }}
                            >
                                <div className="p-5 border-b-4 border-[#0f172a]" style={{ backgroundColor: theme.bg }}>
                                    <h2 className="font-fredoka text-xl font-bold flex items-center gap-2" style={{ color: theme.text }}>
                                        <PawPrint weight="bold" size={22} style={{ color: theme.accent }} />
                                        Animals in This Habitat
                                    </h2>
                                </div>
                                <div className="p-5 grid sm:grid-cols-2 gap-4">
                                    {habitat.animals.map((animal, i) => (
                                        <Link
                                            key={animal.id || i}
                                            to={`/animals/${animal.id}`}
                                            className="flex items-center gap-4 border-4 border-[#0f172a] rounded-xl p-3 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_#0f172a] transition-all"
                                            style={{ backgroundColor: theme.card_bg }}
                                            data-testid={`animal-${animal.id || i}`}
                                        >
                                            {animal.image_url ? (
                                                <img
                                                    src={animal.image_url}
                                                    alt={animal.name}
                                                    crossOrigin="anonymous"
                                                    referrerPolicy="no-referrer"
                                                    className="w-14 h-14 rounded-xl object-cover border-2 border-[#0f172a]"
                                                />
                                            ) : (
                                                <div className="w-14 h-14 rounded-xl flex items-center justify-center border-2 border-[#0f172a]" style={{ backgroundColor: theme.bg }}>
                                                    <PawPrint weight="duotone" size={24} style={{ color: theme.accent }} />
                                                </div>
                                            )}
                                            <div>
                                                <p className="font-fredoka font-bold" style={{ color: theme.text }}>{animal.name}</p>
                                                {animal.species && <p className="text-xs italic opacity-60" style={{ color: theme.text }}>{animal.species}</p>}
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </div>

                    {/* Right column: Fun Facts */}
                    <div className="space-y-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl overflow-hidden sticky top-28"
                            style={{ backgroundColor: theme.card_bg }}
                        >
                            <div className="p-5 border-b-4 border-[#0f172a]" style={{ backgroundColor: theme.bg }}>
                                <h2 className="font-fredoka text-xl font-bold flex items-center gap-2" style={{ color: theme.text }}>
                                    <Lightbulb weight="bold" size={22} style={{ color: theme.accent }} />
                                    Did You Know?
                                </h2>
                            </div>
                            <div className="p-5 space-y-4">
                                {(habitat.fun_facts || []).map((fact, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.3 + i * 0.1 }}
                                        className="flex gap-3"
                                        data-testid={`fun-fact-${i}`}
                                    >
                                        <div
                                            className="w-8 h-8 rounded-full flex-shrink-0 border-3 border-[#0f172a] flex items-center justify-center font-fredoka font-bold text-sm text-white"
                                            style={{ backgroundColor: theme.accent }}
                                        >
                                            {i + 1}
                                        </div>
                                        <p className="text-sm font-semibold leading-relaxed" style={{ color: `${theme.text}cc` }}>
                                            {fact}
                                        </p>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
}
